import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Search,
  FileText,
  Calendar,
  Package,
  Store,
  Hash,
  RotateCcw,
  Loader2,
  CheckCheck,
  ArrowRight,
  Link2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SageProductSelector from '@/components/retail/SageProductSelector';
import { useStoreMapping } from '@/hooks/useStoreMapping';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SKU Exceptions Management - Artigos por Resolver
 * 
 * This component shows WinTouch SKUs that couldn't be matched to SAGE products.
 * Now embedded as a Tab within RetailAuditLog.
 * 
 * Admins can:
 * - View unresolved SKUs with occurrence details
 * - Revalidate against SAGE (after sync)
 * - Map to existing products
 * - Mark as non-stock items
 * 
 * CANONICAL API: /api/sku-exceptions (from routes/sku_exceptions.py)
 */

// Static helper to fetch unresolved count for Badge display
export async function fetchUnresolvedCount() {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/sku-exceptions/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data?.unresolved || 0;
  } catch (err) {
    console.error('Error fetching unresolved count:', err);
    return 0;
  }
}

export default function SkuExceptions() {
  const { getStoreName } = useStoreMapping();
  const [loading, setLoading] = useState(true);
  const [exceptions, setExceptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const urlSearch = new URLSearchParams(window.location.search).get('search') || '';
  const [statusFilter, setStatusFilter] = useState(urlSearch ? '' : 'UNRESOLVED');
  const [storeFilter, setStoreFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [deepLinkSku, setDeepLinkSku] = useState(urlSearch || null);
  
  // Revalidation states
  const [revalidatingAll, setRevalidatingAll] = useState(false);
  const [revalidatingSku, setRevalidatingSku] = useState(null); // SKU currently being revalidated

  // SKU Mapping dialog state
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [mappingException, setMappingException] = useState(null);
  const [mappingForm, setMappingForm] = useState({ sage_sku: '', description: '' });
  const [savingMapping, setSavingMapping] = useState(false);
  const [mappingError, setMappingError] = useState(null);
  // NEW: Option to create permanent mapping vs one-time fix
  const [createPermanentMapping, setCreatePermanentMapping] = useState(false);
  // Product search for autocomplete
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const loadExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (storeFilter) params.set('store_code', storeFilter);
      params.set('limit', '100');

      // CANONICAL API: /api/sku-exceptions/
      const res = await axios.get(`${API}/api/sku-exceptions/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setExceptions(res.data.exceptions || []);
      setTotal(res.data.count || 0);
      setUnresolvedCount(res.data.unresolved_count || 0);
    } catch (err) {
      console.error('Error loading SKU exceptions:', err);
      toast.error('Erro ao carregar artigos por resolver');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, storeFilter]);

  useEffect(() => {
    loadExceptions();
  }, [loadExceptions]);

  // Revalidate a single SKU against the current catalog
  const handleRevalidateSku = useCallback(async (sourceSku) => {
    setRevalidatingSku(sourceSku);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/sku-exceptions/${encodeURIComponent(sourceSku)}/revalidate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.found && res.data.resolved) {
        const classification = res.data.classification;
        const isService = res.data.is_service;
        const createsStock = res.data.creates_stock_movement;
        
        toast.success(
          <div>
            <strong>{sourceSku}</strong> reclassificado como <strong>{classification}</strong>
            {isService && <div className="text-xs mt-1 text-emerald-600">Descritor/Serviço - Não gera movimento de stock</div>}
            {createsStock && <div className="text-xs mt-1 text-blue-600">Produto físico - Reprocessar Stage 2 para criar ISL</div>}
          </div>,
          { duration: 5000 }
        );
        
        // Refresh data to update badges
        await loadExceptions();
      } else {
        toast.info(`SKU "${sourceSku}" ainda não existe no catálogo`, { duration: 3000 });
      }
    } catch (err) {
      console.error('Revalidation error:', err);
      const msg = err.response?.data?.detail || 'Erro ao revalidar SKU';
      toast.error(msg);
    } finally {
      setRevalidatingSku(null);
    }
  }, [loadExceptions]);

  // Revalidate ALL unresolved exceptions
  const handleRevalidateAll = useCallback(async () => {
    setRevalidatingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/sku-exceptions/revalidate-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { resolved_service, resolved_physical, resolved_financial, still_unknown, resolved_skus } = res.data;
      const totalResolved = (resolved_service || 0) + (resolved_physical || 0) + (resolved_financial || 0);
      
      if (totalResolved > 0) {
        toast.success(
          <div>
            <strong>{totalResolved} SKU(s) resolvidos</strong>
            {resolved_service > 0 && <div className="text-xs">• {resolved_service} como SERVICE (sem stock)</div>}
            {resolved_physical > 0 && <div className="text-xs">• {resolved_physical} como PHYSICAL_PRODUCT</div>}
            {resolved_financial > 0 && <div className="text-xs">• {resolved_financial} como FINANCIAL</div>}
            {still_unknown > 0 && <div className="text-xs mt-1 text-amber-600">{still_unknown} ainda por resolver</div>}
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.info(
          still_unknown > 0 
            ? `Nenhum SKU novo encontrado. ${still_unknown} ainda por resolver.`
            : 'Todos os SKUs já estão resolvidos.',
          { duration: 3000 }
        );
      }
      
      // Refresh data
      await loadExceptions();
    } catch (err) {
      console.error('Batch revalidation error:', err);
      const msg = err.response?.data?.detail || 'Erro ao revalidar SKUs';
      toast.error(msg);
    } finally {
      setRevalidatingAll(false);
    }
  }, [loadExceptions]);

  // ============================================================================
  // SKU MAPPING FUNCTIONS
  // ============================================================================
  
  // Search products for autocomplete
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/products`, {
        params: { search: query, limit: 15 },
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns 'items' not 'products' - filter out SERVICE products
      const products = res.data.items || res.data.products || [];
      const physical = products.filter(p => !p.is_service);
      setProductSearchResults(physical);
    } catch (err) {
      console.error('Error searching products:', err);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  }, []);
  
  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);
  
  // Handle product selection from autocomplete
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setMappingForm(prev => ({
      ...prev,
      sage_sku: product.sku,
    }));
    setProductSearch('');
    setProductSearchResults([]);
  };
  
  // Open mapping dialog
  const handleOpenMappingDialog = (exception) => {
    setMappingException(exception);
    setMappingForm({ sage_sku: '', description: '' });
    setSelectedProduct(null);
    setProductSearch('');
    setMappingError(null);
    setCreatePermanentMapping(false); // Default: one-time fix
    setShowMappingDialog(true);
  };

  // Deep link from a batch ("Criar mapeamento"): open the mapping for that SKU directly
  useEffect(() => {
    if (!deepLinkSku || loading) return;
    const match = exceptions.find((e) => (e.source_sku || '').trim().toUpperCase() === deepLinkSku.trim().toUpperCase()
      && e.status === 'UNRESOLVED');
    if (match) handleOpenMappingDialog(match);
    setDeepLinkSku(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkSku, loading, exceptions]);
  
  // Save mapping and resolve exception
  const handleSaveMapping = async () => {
    if (!mappingForm.sage_sku || !mappingException) {
      setMappingError('Selecione um artigo SAGE');
      return;
    }
    
    setSavingMapping(true);
    setMappingError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // RESOLVE THE EXCEPTION with the new API that distinguishes:
      // - One-time fix (create_permanent_mapping: false) - DEFAULT
      // - Permanent mapping (create_permanent_mapping: true) - EXPLICIT CHOICE
      await axios.post(
        `${API}/api/sku-exceptions/${encodeURIComponent(mappingException.source_sku)}/resolve`,
        {
          resolution_type: 'SKU_MAPPED',
          sage_sku: mappingForm.sage_sku,
          notes: mappingForm.description || `Corrigido para ${selectedProduct?.name || mappingForm.sage_sku}`,
          create_permanent_mapping: createPermanentMapping, // NEW: User's explicit choice
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        <div>
          <strong>{createPermanentMapping ? 'Mapeamento permanente criado' : 'Correção pontual aplicada'}</strong>
          <div className="text-xs mt-1">
            {mappingException.source_sku} → {mappingForm.sage_sku}
          </div>
          {!createPermanentMapping && (
            <div className="text-xs text-slate-500 mt-1">
              Futuras ocorrências deste SKU não serão afetadas
            </div>
          )}
        </div>,
        { duration: 5000 }
      );
      
      setShowMappingDialog(false);
      setCreatePermanentMapping(false); // Reset for next time
      await loadExceptions();
    } catch (err) {
      console.error('Mapping error:', err);
      setMappingError(err.response?.data?.detail || 'Erro ao aplicar correção');
    } finally {
      setSavingMapping(false);
    }
  };

  // Filter by search query
  const filteredExceptions = exceptions.filter(exc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      exc.source_sku?.toLowerCase().includes(q) ||
      exc.source_description?.toLowerCase().includes(q) ||
      exc.store_code?.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status, resolutionType) => {
    // Check for auto-resolved states
    if (status === 'RESOLVED' && resolutionType) {
      if (resolutionType.includes('RECLASSIFIED_SERVICE')) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <CheckCheck className="w-3 h-3" />
            SERVICE
          </span>
        );
      }
      if (resolutionType.includes('RECLASSIFIED_PHYSICAL')) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCheck className="w-3 h-3" />
            Físico
          </span>
        );
      }
      if (resolutionType.includes('RECLASSIFIED_FINANCIAL')) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
            <CheckCheck className="w-3 h-3" />
            Financeiro
          </span>
        );
      }
    }
    
    switch (status) {
      case 'UNRESOLVED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <AlertTriangle className="w-3 h-3" />
            Por Resolver
          </span>
        );
      case 'MAPPED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="w-3 h-3" />
            Mapeado
          </span>
        );
      case 'RESOLVED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <CheckCircle className="w-3 h-3" />
            Resolvido
          </span>
        );
      case 'NON_STOCK':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
            Non-Stock
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6" data-testid="sku-exceptions-page">
        {/* Header with Revalidate All button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Artigos por Resolver</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              SKUs WinTouch não encontrados no catálogo SAGE
            </p>
          </div>
          
          {/* Revalidate All button */}
          <button
            onClick={handleRevalidateAll}
            disabled={revalidatingAll || unresolvedCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="revalidate-all-btn"
          >
            {revalidatingAll ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                A revalidar...
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4" />
                Revalidar todos ({unresolvedCount})
              </>
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Pesquisar SKU ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-sku-input"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
              data-testid="status-filter"
            >
              <option value="">Todos os estados</option>
              <option value="UNRESOLVED">Por Resolver</option>
              <option value="MAPPED">Mapeados</option>
              <option value="RESOLVED">Resolvidos</option>
              <option value="NON_STOCK">Non-Stock</option>
            </select>

            {/* Store Filter */}
            <select
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
              data-testid="store-filter"
            >
              <option value="">Todas as lojas</option>
              <option value="NORTESH">NORTESH</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-xl font-bold text-slate-900">{total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">SKUs Únicos</p>
                <p className="text-xl font-bold text-slate-900">
                  {new Set(exceptions.map(e => e.source_sku)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Store className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Lojas Afetadas</p>
                <p className="text-xl font-bold text-slate-900">
                  {new Set(exceptions.map(e => e.store_code)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Hash className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ocorrências</p>
                <p className="text-xl font-bold text-slate-900">
                  {exceptions.reduce((sum, e) => sum + (e.occurrence_count || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Exceptions Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredExceptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="no-exceptions">
              <CheckCircle className="w-12 h-12 text-emerald-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Sem artigos por resolver</h3>
              <p className="text-sm text-slate-500">
                Todos os SKUs do WinTouch foram identificados no catálogo.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Loja
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ocorrências
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Quantidade
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Primeira Ocorrência
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredExceptions.map((exc, idx) => (
                    <tr 
                      key={`${exc.source_sku}-${getStoreName(exc.store_code)}-${idx}`}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`exception-row-${exc.source_sku}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">
                          {exc.source_sku}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">
                        {exc.source_description || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-sm text-slate-700">
                          <Store className="w-3.5 h-3.5 text-slate-400" />
                          {getStoreName(exc.store_code)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {exc.occurrence_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-slate-700">
                        {exc.total_quantity?.toFixed(1) || '0.0'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(exc.first_seen_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(exc.status, exc.resolution_type)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {exc.status === 'UNRESOLVED' ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleRevalidateSku(exc.source_sku)}
                              disabled={revalidatingSku === exc.source_sku || revalidatingAll}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              data-testid={`revalidate-btn-${exc.source_sku}`}
                              title="Revalidar contra o catálogo"
                            >
                              {revalidatingSku === exc.source_sku ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                            </button>
                            <button
                              onClick={() => handleOpenMappingDialog(exc)}
                              disabled={revalidatingAll}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              data-testid={`map-btn-${exc.source_sku}`}
                              title="Mapear para artigo SAGE"
                            >
                              <Link2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Info Note */}
      {filteredExceptions.length === 0 && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Como funcionam os Artigos por Resolver?</h4>
              <p className="mt-1 text-sm text-blue-700">
                Quando um ficheiro WinTouch contém um SKU que não existe no catálogo SAGE sincronizado, 
                esse artigo é registado aqui para resolução. Pode criar o artigo no SAGE e depois executar 
                uma nova sincronização, ou mapear manualmente para um artigo existente.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* SKU Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Mapear para Artigo SAGE
            </DialogTitle>
            <DialogDescription>
              Criar mapeamento permanente entre o SKU WinTouch e um artigo do catálogo SAGE.
            </DialogDescription>
          </DialogHeader>
          
          {mappingError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {mappingError}
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Source SKU info */}
            <div className="bg-slate-50 rounded-lg p-3">
              <Label className="text-xs text-slate-500">SKU WinTouch</Label>
              <div className="font-mono font-medium text-lg">{mappingException?.source_sku}</div>
              {mappingException?.source_description && (
                <div className="text-sm text-slate-600 mt-1">{mappingException.source_description}</div>
              )}
            </div>
            
            <div className="flex items-center justify-center text-slate-400">
              <ArrowRight className="w-6 h-6" />
            </div>
            
            {/* SAGE SKU with autocomplete */}
            {/* SAGE SKU with shared SageProductSelector */}
            <SageProductSelector
              label="Artigo SAGE"
              placeholder="Pesquisar artigo no catálogo..."
              value={selectedProduct}
              onChange={(product) => {
                setSelectedProduct(product);
                setMappingForm(prev => ({
                  ...prev,
                  sage_sku: product?.sku || '',
                }));
              }}
              disabled={savingMapping}
              error={mappingError && !selectedProduct ? "Selecione um artigo SAGE válido" : null}
              data-testid="sage-product-selector-exception"
            />
            
            {/* MAPPING TYPE SELECTION - One-time fix vs Permanent mapping */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="text-sm font-medium">Tipo de correção</Label>
              
              <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-slate-50">
                <input
                  type="radio"
                  name="mappingType"
                  checked={!createPermanentMapping}
                  onChange={() => setCreatePermanentMapping(false)}
                  className="mt-1"
                  data-testid="one-time-fix-radio"
                />
                <div>
                  <div className="font-medium text-sm">Corrigir apenas esta ocorrência</div>
                  <div className="text-xs text-slate-500">
                    Futuras importações deste SKU continuarão a aparecer em "Artigos por Resolver"
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-slate-50">
                <input
                  type="radio"
                  name="mappingType"
                  checked={createPermanentMapping}
                  onChange={() => setCreatePermanentMapping(true)}
                  className="mt-1"
                  data-testid="permanent-mapping-radio"
                />
                <div>
                  <div className="font-medium text-sm">Criar mapeamento permanente</div>
                  <div className="text-xs text-slate-500">
                    Todas as futuras importações de <span className="font-mono">{mappingException?.source_sku}</span> serão automaticamente convertidas para o SKU SAGE selecionado
                  </div>
                </div>
              </label>
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="mapping-notes">Notas (opcional)</Label>
              <Input
                id="mapping-notes"
                placeholder="Motivo da correção..."
                value={mappingForm.description}
                onChange={(e) => setMappingForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="mapping-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveMapping} 
              disabled={savingMapping || !selectedProduct}
              data-testid="save-mapping-btn"
            >
              {savingMapping ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A guardar...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {createPermanentMapping ? 'Criar Mapeamento Permanente' : 'Aplicar Correção'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
